
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';
import ContentSwitcher from '../../../components/docs/ContentSwitcher';
import Terminal from 'plaid-threads/Icons/Terminal';
export const metadata = {
  toc: true,
  layout: 'reference',
  description: 'Use this guide to learn how to integrate with the Plaid Link JavaScript and React SDKs for web apps.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const Schema = makeShortcode("Schema");
const MDXCodeBlock = makeShortcode("MDXCodeBlock");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">


    <Header title='Link Web SDK' subtitle='Reference for integrating with the Link JavaScript SDK and React SDK' mdxType="Header" />
    <Callout icon={<Terminal mdxType="Terminal" />} mdxType="Callout">
      <p>{`Prefer to learn with code examples? Check out our `}<a parentName="p" {...{
          "href": "https://github.com/plaid/tiny-quickstart"
        }}>{`GitHub repo`}</a>{` with working example Link implementations for both `}<a parentName="p" {...{
          "href": "https://github.com/plaid/tiny-quickstart/tree/main/vanilla_js"
        }}>{`JavaScript`}</a>{` and `}<a parentName="p" {...{
          "href": "https://github.com/plaid/tiny-quickstart/tree/main/react"
        }}>{`React`}</a>{`.`}</p>
    </Callout>
    <h4 id="installation" className="no-sticky">Installation</h4>
    <ContentSwitcher.Container id='web' names={['JavaScript', 'React']} hiddenIsSearchable={true}>
      <ContentSwitcher.Content>
        <p>{`Include the Plaid Link initialize script on each page of your site. It must always be loaded directly from `}<inlineCode parentName="p">{`https://cdn.plaid.com`}</inlineCode>{`, rather than included in a bundle or hosted yourself. Unlike Plaid's other SDKs, the JavaScript web SDK is not versioned; `}<inlineCode parentName="p">{`cdn.plaid.com`}</inlineCode>{` will automatically provide the latest available SDK.`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-html",
            "metastring": "switcher=false #index.html",
            "switcher": "false",
            "#index.html": true
          }}>{`<script src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"></script>
`}</code></pre>
  </ContentSwitcher.Content>
      <ContentSwitcher.Content>
        <p>{`To get started with Plaid Link for React, clone the `}<a parentName="p" {...{
            "href": "https://github.com/plaid/react-plaid-link"
          }}>{`GitHub repository`}</a>{` and review the example application and README, which provide reference implementations.`}</p>
        <p>{`Next, you'll need to install the react-plaid-link package.`}</p>
        <p>{`With npm:`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-bash",
            "metastring": "switcher=false #Install with npm",
            "switcher": "false",
            "#Install": true,
            "with": true,
            "npm": true
          }}>{`npm install --save react-plaid-link
`}</code></pre>
        <p>{`With yarn:`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-bash",
            "metastring": "switcher=false #Install with yarn",
            "switcher": "false",
            "#Install": true,
            "with": true,
            "yarn": true
          }}>{`yarn add react-plaid-link
`}</code></pre>
        <p>{`Then import the necessary components and types:`}</p>
        <pre><code parentName="pre" {...{
            "className": "language-tsx",
            "metastring": "switcher=false #Import Link components",
            "switcher": "false",
            "#Import": true,
            "Link": true,
            "components": true
          }}>{`import {
  usePlaidLink,
  PlaidLinkOptions,
  PlaidLinkOnSuccess,
} from 'react-plaid-link';
`}</code></pre>
      </ContentSwitcher.Content>
    </ContentSwitcher.Container>
    <h4 id="csp-directives" className="no-sticky">CSP directives</h4>
    <p>{`If you are using a Content Security Policy (CSP), use the following directives to allow Link traffic:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "switcher=false #CSP Directives",
        "switcher": "false",
        "#CSP": true,
        "Directives": true
      }}>{`default-src https://cdn.plaid.com/;
script-src 'unsafe-inline' https://cdn.plaid.com/link/v2/stable/link-initialize.js;
style-src 'unsafe-inline';
style-src-elem 'unsafe-inline';
style-src-attr 'unsafe-inline';
frame-src https://cdn.plaid.com/;
connect-src https://production.plaid.com/;
`}</code></pre>
    <p>{`If using Sandbox instead of Production, make sure to update the `}<inlineCode parentName="p">{`connect-src`}</inlineCode>{` directive to point to the appropriate server (`}<inlineCode parentName="p">{`https://sandbox.plaid.com`}</inlineCode>{`).`}</p>
    <p>{`If your organization does not allow the use of `}<inlineCode parentName="p">{`unsafe-inline`}</inlineCode>{`, use a `}<a parentName="p" {...{
        "href": "https://content-security-policy.com/nonce/"
      }}>{`CSP nonce`}</a>{` generated for the page response instead:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "switcher=false #CSP Directives",
        "switcher": "false",
        "#CSP": true,
        "Directives": true
      }}>{`default-src https://cdn.plaid.com/;
script-src 'nonce-<PAGE_RESPONSE_NONCE>' https://cdn.plaid.com/link/v2/stable/link-initialize.js;
style-src 'nonce-<PAGE_RESPONSE_NONCE>';
style-src-elem 'nonce-<PAGE_RESPONSE_NONCE>';
style-src-attr 'unsafe-inline';
frame-src https://cdn.plaid.com/;
connect-src https://production.plaid.com/;
`}</code></pre>
    <p>{`Then reference the same nonce value on the Plaid Link script tag:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-html",
        "metastring": "switcher=false #Link script tag",
        "switcher": "false",
        "#Link": true,
        "script": true,
        "tag": true
      }}>{`<script
  nonce="<PAGE_RESPONSE_NONCE>"
  src="https://cdn.plaid.com/link/v2/stable/link-initialize.js"
></script>
`}</code></pre>
    <h4 id="creating-a-link-token" className="no-sticky">Creating a Link token</h4>
    <p>{`Before you can create an instance of Link, you need to first create a `}<inlineCode parentName="p">{`link_token`}</inlineCode>{`. A `}<inlineCode parentName="p">{`link_token`}</inlineCode>{` can be configured for
different Link flows and is used to control much of Link's behavior. To learn how to create a new
`}<inlineCode parentName="p">{`link_token`}</inlineCode>{`, see the API Reference entry for `}<inlineCode parentName="p">{`/link/token/create`}</inlineCode>{`.`}</p>
    <h4 {...{
      "id": "create"
    }}><inlineCode parentName="h4">{`create()`}</inlineCode></h4>
    <Schema type='link' sdk='web' method='create' mdxType="Schema">
      <MDXCodeBlock type='frontend' title="Create example" mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-javascript"
          }}>{`const handler = Plaid.create({
  token: 'GENERATED_LINK_TOKEN',
  onSuccess: (public_token, metadata) => {},
  onLoad: () => {},
  onExit: (err, metadata) => {},
  onEvent: (eventName, metadata) => {},
});
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-tsx"
          }}>{`// The usePlaidLink hook manages Plaid Link creation
// It does not return a destroy function;
// instead, on unmount it automatically destroys the Link instance
const config: PlaidLinkOptions = {
  onSuccess: (public_token, metadata) => {},
  onExit: (err, metadata) => {},
  onEvent: (eventName, metadata) => {},
  token: 'GENERATED_LINK_TOKEN',
};

const { open, exit, ready } = usePlaidLink(config);
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <h4 {...{
      "id": "onsuccess"
    }}><inlineCode parentName="h4">{`onSuccess`}</inlineCode></h4>
    <Schema type='link' sdk='web' method='onSuccess' mdxType="Schema">
  <MDXCodeBlock type='frontend' title="onSuccess example" mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-tsx"
          }}>{`import {
  PlaidLinkOnSuccess,
  PlaidLinkOnSuccessMetadata,
} from 'react-plaid-link';

const onSuccess = useCallback<PlaidLinkOnSuccess>(
  (public_token: string | null, metadata: PlaidLinkOnSuccessMetadata) => {
    // log and save metadata
    // public_token is null for products that don't create an Item, e.g. Identity Verification
    if (public_token) {
      // exchange public token (if using Item-based products)
      fetch('//yourserver.com/exchange-public-token', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          public_token,
        }),
      });
    }
  },
  [],
);
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-javascript"
          }}>{`const handler = Plaid.create({
  ...,
  onSuccess: (public_token, metadata) => {
    // log and save metadata
    // exchange public token (if using Item-based products)
    fetch('//yourserver.com/get_access_token', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        public_token: public_token,
      }),
    });
  }
});
`}</code></pre>
      </MDXCodeBlock>
      <pre><code parentName="pre" {...{
          "className": "language-javascript",
          "metastring": "switcher=false #Metadata schema",
          "switcher": "false",
          "#Metadata": true,
          "schema": true
        }}>{`{
  institution: {
    name: 'Wells Fargo',
    institution_id: 'ins_4'
  },
  accounts: [
    {
      id: 'ygPnJweommTWNr9doD6ZfGR6GGVQy7fyREmWy',
      name: 'Plaid Checking',
      mask: '0000',
      type: 'depository',
      subtype: 'checking',
      verification_status: null
    },
    {
      id: '9ebEyJAl33FRrZNLBG8ECxD9xxpwWnuRNZ1V4',
      name: 'Plaid Saving',
      mask: '1111',
      type: 'depository',
      subtype: 'savings'
    }
    ...
  ],
  link_session_id: '79e772be-547d-4c9c-8b76-4ac4ed4c441a'
}
`}</code></pre>
    </Schema>
    <h4 {...{
      "id": "onexit"
    }}><inlineCode parentName="h4">{`onExit`}</inlineCode></h4>
    <Schema type='link' sdk='web' method="onExit" mdxType="Schema">
      <MDXCodeBlock type='frontend' title="onExit example" mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-tsx"
          }}>{`import {
  PlaidLinkOnExit,
  PlaidLinkOnExitMetadata,
  PlaidLinkError,
} from 'react-plaid-link';

const onExit = useCallback<PlaidLinkOnExit>(
  (error: PlaidLinkError | null, metadata: PlaidLinkOnExitMetadata) => {
    // log and save error and metadata
    // handle invalid link token
    if (error != null && error.error_code === 'INVALID_LINK_TOKEN') {
      // generate new link token
    }
    // to handle other error codes, see https://plaid.com/docs/errors/
  },
  [],
);
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-javascript"
          }}>{`const handler = Plaid.create({
  ...,
  onExit: (error, metadata) => {
    // Save data from the onExit handler
    supportHandler.report({
      error: error,
      institution: metadata.institution,
      link_session_id: metadata.link_session_id,
      plaid_request_id: metadata.request_id,
      status: metadata.status,
    });
  },
});
`}</code></pre>
      </MDXCodeBlock>
      <pre><code parentName="pre" {...{
          "className": "language-javascript",
          "metastring": "switcher=false #Error schema",
          "switcher": "false",
          "#Error": true,
          "schema": true
        }}>{`{
  error_type: 'ITEM_ERROR',
  error_code: 'INVALID_CREDENTIALS',
  error_message: 'the provided credentials were not correct',
  display_message: 'The provided credentials were not correct. Please try again.',
}
`}</code></pre>
      <pre><code parentName="pre" {...{
          "className": "language-javascript",
          "metastring": "switcher=false #Metadata schema",
          "switcher": "false",
          "#Metadata": true,
          "schema": true
        }}>{`{
  institution: {
    name: 'Wells Fargo',
    institution_id: 'ins_4'
  },
  status: 'requires_credentials',
  link_session_id: '36e201e0-2280-46f0-a6ee-6d417b450438',
  request_id: '8C7jNbDScC24THu'
}
`}</code></pre>
    </Schema>
    <h4 {...{
      "id": "onevent"
    }}><inlineCode parentName="h4">{`onEvent`}</inlineCode></h4>
    <Schema type='link' sdk='web' method="onEvent" mdxType="Schema">
      <MDXCodeBlock type='frontend' title="onEvent example" mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-tsx"
          }}>{`import {
  PlaidLinkOnEvent,
  PlaidLinkOnEventMetadata,
  PlaidLinkStableEvent,
} from 'react-plaid-link';

const onEvent = useCallback<PlaidLinkOnEvent>(
  (
    eventName: PlaidLinkStableEvent | string,
    metadata: PlaidLinkOnEventMetadata,
  ) => {
    // log eventName and metadata
  },
  [],
);
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-javascript"
          }}>{`const handler = Plaid.create({
  ...,
  onEvent: (eventName, metadata) => {
    // send event and metadata to self-hosted analytics
    analytics.send(eventName, metadata);
  },
});
`}</code></pre>
      </MDXCodeBlock>
      <pre><code parentName="pre" {...{
          "className": "language-javascript",
          "metastring": "switcher=false #Metadata schema",
          "switcher": "false",
          "#Metadata": true,
          "schema": true
        }}>{`{
  error_type: 'ITEM_ERROR',
  error_code: 'INVALID_CREDENTIALS',
  error_message: 'the provided credentials were not correct',
  exit_status: null,
  institution_id: 'ins_4',
  institution_name: 'Wells Fargo',
  institution_search_query: 'wellsf',
  mfa_type: null,
  view_name: 'ERROR',
  request_id: 'm8MDnv9okwxFNBV',
  link_session_id: '30571e9b-d6c6-42ee-a7cf-c34768a8f62d',
  timestamp: '2017-09-14T14:42:19.350Z',
  selection: null,
}
`}</code></pre>
    </Schema>
    <h4 {...{
      "id": "open"
    }}><inlineCode parentName="h4">{`open()`}</inlineCode></h4>
    <Schema type='link' sdk='web' method='open' mdxType="Schema">
      <MDXCodeBlock type='frontend' title="open example" mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-javascript"
          }}>{`const handler = Plaid.create({ ... });

// Open Link
handler.open();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-tsx"
          }}>{`const { open, exit, ready } = usePlaidLink(config);

// Open Link
if (ready) {
  open();
}
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <h4 {...{
      "id": "exit"
    }}><inlineCode parentName="h4">{`exit()`}</inlineCode></h4>
    <Schema type='link' sdk='web' method='exit' mdxType="Schema">
  <MDXCodeBlock type='frontend' title="Graceful exit example" mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-javascript"
          }}>{`const handler = Plaid.create({ ... });

// Graceful exit - Link may display a confirmation screen
// depending on how far the user is in the flow
handler.exit();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-tsx"
          }}>{`const { open, exit, ready } = usePlaidLink(config);

// Graceful exit - Link may display a confirmation screen
// depending on how far the user is in the flow
exit();
`}</code></pre>
      </MDXCodeBlock>
      <MDXCodeBlock type='frontend' title="Forced exit example" mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-javascript"
          }}>{`const handler = Plaid.create({ ... });

// Force exit - Link exits immediately
handler.exit({ force: true });
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-tsx"
          }}>{`const { open, exit, ready } = usePlaidLink(config);

// Force exit - Link exits immediately
exit({ force: true });
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <h4 {...{
      "id": "destroy"
    }}><inlineCode parentName="h4">{`destroy()`}</inlineCode></h4>
    <Schema type='link' sdk='web' method='destroy' mdxType="Schema">
  <MDXCodeBlock type='frontend' title="Destroy example" mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-javascript"
          }}>{`const handler = Plaid.create({ ... })

// Destroy and clean up the Link handler & iFrame
handler.destroy();
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-tsx"
          }}>{`// On unmount usePlaidLink hook automatically destroys any
// existing link instance
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <h4 {...{
      "id": "submit"
    }}><inlineCode parentName="h4">{`submit()`}</inlineCode></h4>
    <Schema type='link' sdk='web' method='submit' mdxType="Schema">
      <MDXCodeBlock type='frontend' title="Submit example" mdxType="MDXCodeBlock">
        <pre><code parentName="pre" {...{
            "className": "language-javascript"
          }}>{`const handler = Plaid.create({ ... })

// After collecting a user phone number, submit it to Link
handler.submit({
  "phone_number": "+14155550123"
});
`}</code></pre>
        <pre><code parentName="pre" {...{
            "className": "language-tsx"
          }}>{`const { open, exit, submit } = usePlaidLink(config);

// After collecting a user phone number...
submit({
    "phone_number": "+14155550123"
});
`}</code></pre>
      </MDXCodeBlock>
    </Schema>
    <h4 {...{
      "id": "oauth"
    }}>{`OAuth`}</h4>
    <p>{`Using Plaid Link with an OAuth flow requires some additional setup instructions. For details, see the `}<a parentName="p" {...{
        "href": "/docs/link/oauth"
      }}>{`OAuth Guide`}</a>{`.`}</p>
    <h4 {...{
      "id": "supported-browsers"
    }}>{`Supported browsers`}</h4>
    <p>{`Plaid officially supports Link on the latest versions of Chrome, Firefox, Safari, and Edge. Browsers are supported on Windows, Mac, Linux, iOS, and Android. Previous browser versions are also supported, as long as they are actively maintained; Plaid does not support browser versions that are no longer receiving patch updates, or that have been assigned official end of life (EOL) or end of support (EOS) status.`}</p>
    <p>{`Ad-blocking software is not officially supported with Link web, and some ad-blockers are known to cause conflicts with Link.`}</p>
    <h4 {...{
      "id": "example-code-in-plaid-pattern"
    }}>{`Example code in Plaid Pattern`}</h4>
    <p>{`For a real-life example of using Plaid Link for React, see `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern/blob/master/client/src/components/LaunchLink.tsx"
      }}>{`LaunchLink.tsx`}</a>{`. This file illustrates the code for implementation of Plaid Link for React for the Node-based `}<a parentName="p" {...{
        "href": "https://github.com/plaid/pattern"
      }}>{`Plaid Pattern`}</a>{` sample app.`}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "create",
        level: 4,
        title: "create()",
      },
{
        id: "onsuccess",
        level: 4,
        title: "onSuccess",
      },
{
        id: "onexit",
        level: 4,
        title: "onExit",
      },
{
        id: "onevent",
        level: 4,
        title: "onEvent",
      },
{
        id: "open",
        level: 4,
        title: "open()",
      },
{
        id: "exit",
        level: 4,
        title: "exit()",
      },
{
        id: "destroy",
        level: 4,
        title: "destroy()",
      },
{
        id: "submit",
        level: 4,
        title: "submit()",
      },
{
        id: "oauth",
        level: 4,
        title: "OAuth",
      },
{
        id: "supported-browsers",
        level: 4,
        title: "Supported browsers",
      },
{
        id: "example-code-in-plaid-pattern",
        level: 4,
        title: "Example code in Plaid Pattern",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
